import { hexToRgbArray } from './colorUtil';
import { getCourseBackgroundColor, getCourseColor } from './courseColors';

/**
 * Signal colors for special lesson/substitution types ("untis events"), which
 * are rendered with a fixed color for easier distinction from regular courses.
 */
export const TIMETABLE_UNTIS_EVENT_COLORS = {
  light: { background: '#ffb8b8', border: '#fe6b6b' },
  dark: { background: '#fa9191', border: '#b46363' },
} as const;

/** Period colors are dimmed in the dark theme to reduce their brightness. */
const DARK_THEME_OPACITY = '0.4';
const LIGHT_THEME_OPACITY = '1';

const TEXT_COLOR_DARK = '#fff';
const TEXT_COLOR_LIGHT = 'rgb(37, 36, 35)';

export interface TimetableEventColors {
  /** Background of the period, a lightened version of the course color. */
  backgroundColor: string;
  /** Signal color of the period, used as border and as colored bar in widgets. */
  borderColor: string;
  /** Text color with enough contrast on the background. */
  textColor: string;
}

/** The lesson fields needed to derive the colors of a period. */
export interface TimetableColorLesson {
  azureId?: string | null;
  id?: string | null;
  color?: string | null;
  /** True for "untis events", which use the fixed signal colors. */
  event?: boolean;
}

/**
 * Derives the colors of a timetable period.
 *
 * Shared by the web-app timetable (FullCalendar grid and dashboard widget) and
 * the Teams cockpit widget, so a lesson looks the same in every app.
 *
 * Note that the colors always have to be derived from the *original* lesson and
 * never from a substitution lesson, so a substituted period keeps the color of
 * its course.
 */
export const getTimetableEventColors = (
  lesson: TimetableColorLesson,
  isDarkTheme: boolean,
): TimetableEventColors => {
  const opacity = isDarkTheme ? DARK_THEME_OPACITY : LIGHT_THEME_OPACITY;
  const untisEventColors = isDarkTheme
    ? TIMETABLE_UNTIS_EVENT_COLORS.dark
    : TIMETABLE_UNTIS_EVENT_COLORS.light;

  const backgroundColor = lesson.event
    ? untisEventColors.background
    : getCourseBackgroundColor(lesson, opacity);
  const signalColor = lesson.event ? untisEventColors.border : getCourseColor(lesson);
  const [red, green, blue] = hexToRgbArray(signalColor);

  return {
    backgroundColor,
    borderColor: `rgb(${red} ${green} ${blue} / ${opacity})`,
    textColor: isDarkTheme ? TEXT_COLOR_DARK : TEXT_COLOR_LIGHT,
  };
};
